import React, { ReactNode } from 'react';

import {
  ColorValue,
  DimensionValue,
  Modal as ReactNativeModal,
  SafeAreaView,
  StyleProp,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
  ViewStyle,
} from 'react-native';

import { KeyboardAwareView } from 'components/ucl/keyboard-aware-view';
import { useIsDesktopWebBreakpoint } from 'hooks/use-media-query';
import { primitive } from 'styles/constants/primitives';

import { Footer } from './footer';
import { Header, HeaderProps } from './header';

export type ModalHeaderOpts = Omit<HeaderProps, 'children' | 'onCloseIconClick'>;

export type ModalProps = {
  isOpen: boolean;
  header: ModalHeaderOpts | ReactNode;
  footer?: ReactNode;
  footerVariant?: 'light' | 'dark';
  backgroundColor?: ColorValue;
  children: ReactNode;
  webSize?: {
    height?: DimensionValue | undefined;
    width?: DimensionValue | undefined;
    minWidth?: DimensionValue | undefined;
  };
  onClose: () => void;
  modalBodyStyle?: StyleProp<ViewStyle>;
  modalContentStyle?: ViewStyle;
};

export function InlineModal({
  isOpen,
  header,
  children,
  footer = null,
  webSize,
  backgroundColor = Styles.color.white,
  onClose,
  modalBodyStyle,
  modalContentStyle,
}: ModalProps) {
  const isDesktopWeb = useIsDesktopWebBreakpoint();
  const headerVariant =
    header && typeof header === 'object' && 'title' in header && header.variant
      ? header.variant
      : 'dark';

  const headerColor =
    headerVariant === 'dark' ? primitive.bk.$bbqBrown : primitive.bk.$mayoEggWhite;

  return (
    <ReactNativeModal
      animationType={isDesktopWeb ? 'fade' : 'slide'}
      visible={isOpen}
      transparent={true}
      onRequestClose={onClose}
      presentationStyle="overFullScreen"
    >
      <SafeAreaView style={{ flex: 0, backgroundColor: headerColor }} />
      <TouchableWithoutFeedback onPress={onClose}>
        <View
          style={[
            styles.modalContainer,
            { backgroundColor: isDesktopWeb ? 'rgba(0,0,0,0.5)' : headerColor },
          ]}
        >
          <TouchableWithoutFeedback onPress={e => e.stopPropagation()}>
            <View
              style={[
                styles.modalContent,
                {
                  backgroundColor,
                  borderRadius: isDesktopWeb ? primitive.spacing.$3 : 0,
                },
                modalContentStyle,
                webSize &&
                  isDesktopWeb && {
                    height: webSize.height,
                    width: webSize.width,
                    minWidth: webSize.minWidth,
                    maxHeight: '90%',
                  },
              ]}
            >
              {header && typeof header === 'object' && 'title' in header ? (
                <Header {...header} onCloseIconClick={onClose} />
              ) : (
                (header as any)
              )}
              <KeyboardAwareView
                keyboardShouldPersistTaps="handled"
                withScroll
                style={[styles.modalBody, modalBodyStyle]}
              >
                {children}
              </KeyboardAwareView>
              {footer && <Footer>{footer}</Footer>}
            </View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </ReactNativeModal>
  );
}

const styles = StyleSheet.create({
  headerContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    height: 56,
  },
  modalContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalContent: {
    width: '100%',
    height: '100%',
  },
  modalBody: {
    paddingVertical: primitive.spacing.$3,
    paddingHorizontal: primitive.spacing.$4,
    flex: 1,
  },
  iconContainer: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
  },
  footerContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: primitive.bk.$lightBrown,
    paddingVertical: primitive.spacing.$3,
    paddingHorizontal: primitive.spacing.$4,
  },
});
